// Puzzle Studio display rules (contracts/studio.md "Implementation notes"): verification badges, the
// scheduling gate as the API applies it, error codes in plain words, cost estimates.

import { isSupported } from "@lumen/puzzle-render";
import { ApiError } from "../api/client";
import type { PuzzleSource, ReviewState, VerificationResult, VerificationSummary } from "../api/schemas";
import type { ItemStatus, JobStatus, StudioType } from "../api/studioSchemas";
import { addDays, todayUtc } from "./dates";

// ---------------------------------------------------------------- verification badges

export type BadgeState = "unique" | "review" | "reviewed" | "rejected" | "failed" | "build" | "unverified";

export interface Badge {
  state: BadgeState;
  label: string;
  tone: "success" | "warning" | "danger" | "muted" | "info";
  title: string;
}

const BADGES: Record<BadgeState, Omit<Badge, "state">> = {
  unique: { label: "solver ✓ unique", tone: "success", title: "The type's solver found exactly the stated answer." },
  review: { label: "needs review", tone: "warning", title: "No solver for this type: a person must approve it before it can be scheduled." },
  reviewed: { label: "reviewed ✓", tone: "success", title: "No solver; a person approved it." },
  rejected: { label: "rejected", tone: "danger", title: "A person rejected it in review." },
  failed: { label: "failed", tone: "danger", title: "Verification failed: it can't be approved or scheduled until an edit passes." },
  build: { label: "✓ content build", tone: "info", title: "A generator row: the content build only writes solver-verified puzzles." },
  unverified: { label: "unverified", tone: "muted", title: "Not verified yet: re-verify it before scheduling." },
};

const badge = (state: BadgeState): Badge => ({ state, ...BADGES[state] });

/** A puzzle's badge from its (summary or full) verification, review state and source. */
export function verificationBadge(
  v: VerificationSummary | VerificationResult | null | undefined,
  reviewState: ReviewState | undefined,
  source?: PuzzleSource,
): Badge {
  if (!v) {
    if (reviewState === "approved") return badge("reviewed");
    if (reviewState === "rejected") return badge("rejected");
    return badge(source === "generator" ? "build" : "unverified");
  }
  if (!v.ok) return badge("failed");
  if (v.method === "solver") return badge("unique");
  if (reviewState === "approved") return badge("reviewed");
  if (reviewState === "rejected") return badge("rejected");
  return badge("review");
}

/** A job item's badge: the item status first (what the worker decided), then the puzzle's review. */
export function itemBadge(status: ItemStatus, v: VerificationResult, reviewState: ReviewState | undefined): Badge {
  if (status === "rejected_by_verifier") return badge("failed");
  return verificationBadge(v, reviewState ?? (status === "needs_review" ? "pending" : "none"));
}

/**
 * The API's scheduling gate (`scheduleGate`): a solver type must pass its solver; a type without one
 * needs an approved review; generator rows without a stored verification pass.
 */
export function scheduleBlocker(
  v: VerificationSummary | VerificationResult | null | undefined,
  reviewState: ReviewState | undefined,
  source: PuzzleSource | undefined,
): string | null {
  if (!v) {
    if (source === "generator" || reviewState === "approved") return null;
    return "It hasn't been verified yet (Re-verify) or approved in review.";
  }
  if (v.method === "solver") return v.ok ? null : "Its solver verification failed. Fix it with Edit spec first.";
  if (!v.ok) return "Its structural checks failed. Fix it with Edit spec first.";
  return reviewState === "approved" ? null : "It has no solver, so a person must approve it first (Approve).";
}

/** Approving never overrides a failed verification (409 not_verified). */
export const approvalBlocker = (v: VerificationSummary | VerificationResult | null | undefined) =>
  v && !v.ok ? (v.method === "solver" ? "Its solver verification failed." : "Its structural checks failed.") : null;

// ---------------------------------------------------------------- jobs

export const JOB_STATUS_TONE: Record<JobStatus, string> = {
  queued: "muted",
  running: "accent",
  done: "success",
  failed: "danger",
  cancelled: "neutral",
};

export const KIND_LABEL: Record<string, string> = { procedural: "procedural", ai: "Claude", import: "import", levelpack: "level pack" };

export const isActiveJob = (status: JobStatus) => status === "queued" || status === "running";

/** `budget_exceeded: The monthly…` → { code, message }. */
export function splitJobError(error: string | null): { code: string | null; message: string } | null {
  if (!error) return null;
  const m = /^([a-z_]+):\s*(.*)$/s.exec(error);
  return m ? { code: m[1]!, message: m[2]! } : { code: null, message: error };
}

// ---------------------------------------------------------------- errors

export const STUDIO_ERRORS: Record<string, { title: string; hint: string }> = {
  engine_unavailable: {
    title: "The puzzle engine is unreachable",
    hint: "Start it (cd engine && .venv/bin/python -m iqengine) and check that ENGINE_SECRET matches the API's.",
  },
  ai_unavailable: {
    title: "Claude isn't available on the engine",
    hint: "The engine has no ANTHROPIC_API_KEY (or it was rejected). Procedural jobs still work.",
  },
  ai_disabled: {
    title: "The AI kill switch is off",
    hint: "Claude drafts need studio.aiEnabled. An admin can turn it on from the Studio overview.",
  },
  budget_exceeded: {
    title: "This month's Claude budget is spent",
    hint: "An admin can raise studio.monthlyBudgetUsd in Config, or wait for the next UTC month.",
  },
  not_verified: {
    title: "Not verified",
    hint: "A failed verification can't be approved or scheduled, and a type without a solver needs an approved review first.",
  },
  conflict: {
    title: "Refused by the schedule rules",
    hint: "Only days after UTC today + 1, and never a day whose puzzle is approved, published or retired.",
  },
  forbidden: { title: "Not allowed for your role", hint: "Studio changes need the editor role; the kill switch needs admin." },
  bad_request: { title: "The API refused the request", hint: "" },
};

export function errorInfo(error: unknown): { code: string; status: number | null; title: string; message: string; hint: string } {
  const code = error instanceof ApiError ? error.code : "error";
  const known = STUDIO_ERRORS[code];
  return {
    code,
    status: error instanceof ApiError ? error.status : null,
    title: known?.title ?? "Something went wrong",
    message: error instanceof Error ? error.message : String(error),
    hint: known?.hint ?? "",
  };
}

// ---------------------------------------------------------------- money and tokens

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtUsd = (n: number | null | undefined) => (n === null || n === undefined ? "—" : n > 0 && n < 0.01 ? "<$0.01" : usd.format(n));

export interface Price {
  input: number;
  output: number;
  cacheRead?: number;
  cacheWrite?: number;
}

/** The worker's per-batch estimate (backend/api README "The worker"): 3000 + 600n input, 1500 + 1500n output, batches of 10. */
export function estimateAiCost(count: number, price: Price | undefined): { usd: number; input: number; output: number } | null {
  if (!price || count < 1) return null;
  let input = 0;
  let output = 0;
  for (let left = count; left > 0; left -= 10) {
    const n = Math.min(10, left);
    input += 3000 + 600 * n;
    output += 1500 + 1500 * n;
  }
  return { usd: (input * price.input + output * price.output) / 1_000_000, input, output };
}

/** YYYY-MM of today and the months before it, newest first. */
export function recentMonths(n: number, today = todayUtc()): string[] {
  const out: string[] = [];
  let y = Number(today.slice(0, 4));
  let m = Number(today.slice(5, 7));
  for (let i = 0; i < n; i++) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m -= 1;
    if (m === 0) {
      m = 12;
      y -= 1;
    }
  }
  return out;
}

export const monthLabel = (month: string) =>
  new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T00:00:00Z`));

// ---------------------------------------------------------------- difficulty

export const DIFFICULTY_PRESETS = [
  { id: "any", label: "Any", min: 0, max: 1 },
  { id: "easy", label: "Easy", min: 0, max: 0.4 },
  { id: "medium", label: "Medium", min: 0.4, max: 0.65 },
  { id: "hard", label: "Hard", min: 0.65, max: 1 },
] as const;

export const difficultyWord = (score: number) => (score < 0.4 ? "easy" : score < 0.65 ? "medium" : "hard");

// ---------------------------------------------------------------- types

/** Components this web renderer can't draw yet: a type that needs one can't ship server-only. */
export const newComponents = (t: Pick<StudioType, "components">) => t.components.filter((c) => !isSupported(c));

export const CATEGORY_ORDER = ["pattern", "number", "spatial", "logic", "memory", "speed", "planning", "word"];

export function groupTypes(types: StudioType[]): [string, StudioType[]][] {
  const groups = new Map<string, StudioType[]>();
  for (const t of types) groups.set(t.category, [...(groups.get(t.category) ?? []), t]);
  const rank = (c: string) => (CATEGORY_ORDER.includes(c) ? CATEGORY_ORDER.indexOf(c) : CATEGORY_ORDER.length);
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([c, ts]) => [c, [...ts].sort((a, b) => a.type.localeCompare(b.type))]);
}

// ---------------------------------------------------------------- scheduling

/** The first day the API lets a bank puzzle take: after UTC today + 1. */
export const firstSchedulableDay = (today = todayUtc()) => addDays(today, 2);

/** Curated ids embed the sourceId: `bank-pattern-24` → `pattern-24`. */
export const sourceIdOf = (id: string, source: PuzzleSource | undefined) => (source === "curated" && id.startsWith("bank-") ? id.slice(5) : null);

/** Option letters in an explanation (the engine refuses them: players see their own option order). */
export function optionLetterRefs(explanation: string): string[] {
  const refs = new Set<string>();
  for (const m of explanation.matchAll(/\b(?:option|answer|choice)s?\s+([A-F])\b|\(([A-F])\)/g)) refs.add((m[1] ?? m[2])!);
  return [...refs].sort();
}
